/**
 * S0 keepsake client: preview binding, export, polling, delivery
 * (PC-B rev 9, sections 10 + 10a.0).
 *
 * `beginKeepsakePreview` synchronously reads the resolved central auth
 * store and rejects unless authenticated; captures owner + auth epoch +
 * the durable local prayer epoch; fails closed on any unresolved local
 * mutation or validity intent (the sheet shows the locked pending copy);
 * then calls the authenticated preview endpoint. The response is
 * DISCARDED unless owner and both epochs still match. Approval reuses
 * that exact response and never recaptures owner, epoch, membership,
 * display, or content. Every await is followed by a binding recheck, and
 * every result acceptance is preceded by the mandatory durable IDB
 * reread (readKeepsakeGate). Bindings never survive reload.
 */

import { getSupabase } from '@/lib/supabase';
import {
  CR_BASELINE_FRONTIER,
  CR_PROJECTION_DIGEST,
  IDENTITY_MODE_CR_BASELINE,
  type KeepsakeLocale,
} from './constants';
import { getAuthEpoch, getAuthUserId } from './epochs';
import { readKeepsakeGate } from './fences';
import { KEEPSAKES_ENABLED } from '@/lib/core-cutover';

// Production opt-in gate. A deployment built without the explicit flag remains
// inert: each entry point throws before any token read or fetch, and UI callers
// are gated on the same KEEPSAKES_ENABLED constant.
function assertKeepsakesEnabled(op: string): void {
  if (!KEEPSAKES_ENABLED) {
    throw new Error(`keepsakes disabled: rejected '${op}'`);
  }
}

export interface KeepsakeRenderPrayerV1 {
  id: string;
  sourceUpdatedAt: string;
  rawText: string;
  createdAt: string;
  renderedCreatedLabel: string;
  status: 'Open' | 'In Progress' | 'Answered' | 'Moving';
  renderedProgress: Array<{ date: string; renderedDateLabel: string; note: string }>;
  renderedTestimony: string | null;
  selectionPrimaryKey: string;
  selectionMentionKeys: string[];
}

export interface KeepsakeRenderInputV1 {
  renderSchemaVersion: 1;
  rendererVersion: string;
  rendererBundleDigest: string;
  canonicalTargetKey: string;
  documentSubjectName: string;
  displayName: string;
  locale: KeepsakeLocale;
  timeZone: 'UTC';
  footerYear: number;
  coverSpanLabel: string;
  stringsVersion: 'keepsake-strings-v1';
  stringsDigest: string;
  prayers: KeepsakeRenderPrayerV1[];
}

export interface KeepsakePreviewResponseV1 {
  responseVersion: 1;
  previewId: string;
  ownerId: string;
  identityMode: 'cr-baseline-v1';
  identityFrontier: 'cr-baseline-v1:none';
  projectionDigest: string;
  canonicalTargetKey: string;
  documentSubjectName: string;
  displayName: string;
  locale: KeepsakeLocale;
  rendererVersion: string;
  rendererBundleDigest: string;
  footerYear: number;
  stringsVersion: 'keepsake-strings-v1';
  stringsDigest: string;
  validityRevision: string;
  membershipCount: number;
  membershipDigest: string;
  renderInputDigest: string;
  renderInput: KeepsakeRenderInputV1;
  previewDigest: string;
}

export interface KeepsakePreviewBindingV1 {
  previewVersion: 1;
  previewId: string;
  exportAttemptId: string;
  expectedOwner: string;
  authEpoch: number;
  localPrayerEpoch: string;
  identityMode: 'cr-baseline-v1';
  createdAt: string;
  serverPreview: KeepsakePreviewResponseV1 | null;
}

export type KeepsakeClientError =
  | 'not-signed-in'
  | 'protection-pending'
  | 'sync-pending'
  | 'account-changed'
  | 'ownership-review-required'
  | 'no-prayers'
  | 'general-unsupported'
  | 'maintenance'
  | 'client-update-required'
  | 'preview-stale'
  | 'rate-limited'
  | 'email-verification-required'
  | 'invalidated'
  | 'not-found'
  | 'try-again';

export type PreviewResult =
  | { ok: true; binding: KeepsakePreviewBindingV1 }
  | { ok: false; code: KeepsakeClientError };

async function getAccessToken(): Promise<string | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Map a server error slug to the closed client error set (12b mapping). */
function mapServerCode(slug: string): KeepsakeClientError {
  switch (slug) {
    case 'ownership-review-required':
      return 'ownership-review-required';
    case 'no-prayers':
      return 'no-prayers';
    case 'general-keepsake-not-supported':
      return 'general-unsupported';
    case 'maintenance':
      return 'maintenance';
    case 'client-update-required':
    case 'identity-mode-not-active':
      return 'client-update-required';
    case 'preview-stale':
    case 'projection-digest-mismatch':
    case 'attempt-content-mismatch':
      return 'preview-stale';
    case 'rate-limited':
      return 'rate-limited';
    case 'verified-email-required':
      return 'email-verification-required';
    case 'invalidated':
      return 'invalidated';
    case 'not-found':
      return 'not-found';
    case 'owner-mismatch':
    case 'unauthorized':
      return 'account-changed';
    default:
      return 'try-again';
  }
}

/** True while the binding's owner + auth epoch + local epoch all hold. */
async function bindingCurrent(binding: KeepsakePreviewBindingV1): Promise<boolean> {
  if (getAuthEpoch() !== binding.authEpoch) return false;
  if (getAuthUserId() !== binding.expectedOwner) return false;
  const gate = await readKeepsakeGate();
  if (gate.hold.held) return false;
  if (gate.localPrayerEpoch !== binding.localPrayerEpoch) return false;
  return true;
}

export async function beginKeepsakePreview(
  targetWho: string,
  locale: KeepsakeLocale,
): Promise<PreviewResult> {
  assertKeepsakesEnabled('keepsake:preview');
  // Synchronous central-auth read; reject unless authenticated.
  const ownerId = getAuthUserId();
  if (!ownerId) return { ok: false, code: 'not-signed-in' };
  const authEpoch = getAuthEpoch();

  // Conservative local fail-closed gate BEFORE the preview request.
  const gate = await readKeepsakeGate();
  if (gate.hold.held) {
    return {
      ok: false,
      code: gate.hold.reason === 'protection-pending' ? 'protection-pending' : 'sync-pending',
    };
  }

  const binding: KeepsakePreviewBindingV1 = {
    previewVersion: 1,
    previewId: crypto.randomUUID(),
    exportAttemptId: crypto.randomUUID(),
    expectedOwner: ownerId,
    authEpoch,
    localPrayerEpoch: gate.localPrayerEpoch,
    identityMode: IDENTITY_MODE_CR_BASELINE,
    createdAt: new Date().toISOString(),
    serverPreview: null,
  };

  const token = await getAccessToken();
  if (token === null || !(await bindingCurrent(binding))) {
    return { ok: false, code: 'account-changed' };
  }

  let res: Response;
  try {
    res = await fetch('/api/keepsake/preview', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        requestVersion: 1,
        operation: 'preview',
        previewId: binding.previewId,
        expectedOwner: binding.expectedOwner,
        identityMode: IDENTITY_MODE_CR_BASELINE,
        identityFrontier: CR_BASELINE_FRONTIER,
        protocolVersion: 1,
        normalizerVersion: 'who-key-v1',
        foldVersion: 'cr-empty-fold-v1',
        projectionDigest: CR_PROJECTION_DIGEST,
        targetWho,
        locale,
      }),
    });
  } catch {
    return { ok: false, code: 'try-again' };
  }

  // The response is discarded unless owner and BOTH epochs still match
  // (mandatory durable reread inside bindingCurrent).
  if (!(await bindingCurrent(binding))) {
    return { ok: false, code: 'account-changed' };
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    return { ok: false, code: mapServerCode(body?.error ?? 'try-again') };
  }
  const preview = (await res.json().catch(() => null)) as KeepsakePreviewResponseV1 | null;
  if (!preview || preview.responseVersion !== 1 || preview.ownerId !== binding.expectedOwner) {
    return { ok: false, code: 'try-again' };
  }
  if (!(await bindingCurrent(binding))) {
    return { ok: false, code: 'account-changed' };
  }
  return { ok: true, binding: { ...binding, serverPreview: preview } };
}

export type ExportResult =
  | { ok: true; kind: 'pdf'; blob: Blob }
  | { ok: true; kind: 'queued'; jobId: string; expiresAt: string }
  | { ok: false; code: KeepsakeClientError };

/**
 * Approval: reuses the EXACT frozen preview response — never recaptures
 * owner, epoch, membership, display, or content.
 */
export async function requestKeepsakeExport(
  binding: KeepsakePreviewBindingV1,
): Promise<ExportResult> {
  assertKeepsakesEnabled('keepsake:export');
  const preview = binding.serverPreview;
  if (!preview) return { ok: false, code: 'try-again' };
  if (!(await bindingCurrent(binding))) {
    return { ok: false, code: 'account-changed' };
  }
  const token = await getAccessToken();
  if (token === null || !(await bindingCurrent(binding))) {
    return { ok: false, code: 'account-changed' };
  }

  let res: Response;
  try {
    res = await fetch('/api/keepsake/render', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        requestVersion: 1,
        operation: 'export',
        previewId: preview.previewId,
        exportAttemptId: binding.exportAttemptId,
        expectedOwner: binding.expectedOwner,
        identityMode: IDENTITY_MODE_CR_BASELINE,
        identityFrontier: CR_BASELINE_FRONTIER,
        protocolVersion: 1,
        normalizerVersion: 'who-key-v1',
        foldVersion: 'cr-empty-fold-v1',
        projectionDigest: preview.projectionDigest,
        canonicalTargetKey: preview.canonicalTargetKey,
        documentSubjectName: preview.documentSubjectName,
        displayName: preview.displayName,
        locale: preview.locale,
        rendererVersion: preview.rendererVersion,
        rendererBundleDigest: preview.rendererBundleDigest,
        footerYear: preview.footerYear,
        stringsVersion: preview.stringsVersion,
        stringsDigest: preview.stringsDigest,
        validityRevision: preview.validityRevision,
        membershipCount: preview.membershipCount,
        membershipDigest: preview.membershipDigest,
        renderInputDigest: preview.renderInputDigest,
        previewDigest: preview.previewDigest,
      }),
    });
  } catch {
    return { ok: false, code: 'try-again' };
  }

  if (!(await bindingCurrent(binding))) {
    return { ok: false, code: 'account-changed' };
  }

  if (res.status === 202) {
    const body = (await res.json().catch(() => null)) as {
      jobId?: string;
      expiresAt?: string;
    } | null;
    if (!body?.jobId) return { ok: false, code: 'try-again' };
    if (!(await bindingCurrent(binding))) {
      return { ok: false, code: 'account-changed' };
    }
    return { ok: true, kind: 'queued', jobId: body.jobId, expiresAt: body.expiresAt ?? '' };
  }
  if (res.ok) {
    const blob = await res.blob();
    if (!(await bindingCurrent(binding))) {
      // An auth or local prayer state change mid-flight discards the
      // response rather than delivering across accounts.
      return { ok: false, code: 'account-changed' };
    }
    return { ok: true, kind: 'pdf', blob };
  }
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  return { ok: false, code: mapServerCode(body?.error ?? 'try-again') };
}

export type StatusResult =
  | {
      ok: true;
      status: string;
      errorCode: string | null;
      expiresAt: string;
    }
  | { ok: false; code: KeepsakeClientError };

/** Poll status with expectedOwner; epoch-checked before acceptance. */
export async function pollKeepsakeJobStatus(
  binding: KeepsakePreviewBindingV1,
  jobId: string,
): Promise<StatusResult> {
  assertKeepsakesEnabled('keepsake:status');
  if (!(await bindingCurrent(binding))) return { ok: false, code: 'account-changed' };
  const token = await getAccessToken();
  if (token === null) return { ok: false, code: 'account-changed' };
  let res: Response;
  try {
    res = await fetch(`/api/keepsake/status?jobId=${jobId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-adia-expected-owner': binding.expectedOwner,
      },
    });
  } catch {
    return { ok: false, code: 'try-again' };
  }
  if (!(await bindingCurrent(binding))) return { ok: false, code: 'account-changed' };
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    return { ok: false, code: mapServerCode(body?.error ?? 'try-again') };
  }
  const body = (await res.json().catch(() => null)) as {
    status?: string;
    errorCode?: string | null;
    expiresAt?: string;
  } | null;
  if (!body?.status) return { ok: false, code: 'try-again' };
  return {
    ok: true,
    status: body.status,
    errorCode: body.errorCode ?? null,
    expiresAt: body.expiresAt ?? '',
  };
}

export type DeliveryResult =
  | { ok: true; blob: Blob }
  | { ok: false; code: KeepsakeClientError };

export async function fetchKeepsakeDelivery(
  binding: KeepsakePreviewBindingV1,
  jobId: string,
): Promise<DeliveryResult> {
  assertKeepsakesEnabled('keepsake:delivery');
  if (!(await bindingCurrent(binding))) return { ok: false, code: 'account-changed' };
  const token = await getAccessToken();
  if (token === null) return { ok: false, code: 'account-changed' };
  let res: Response;
  try {
    res = await fetch(`/api/keepsake/delivery?jobId=${jobId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-adia-expected-owner': binding.expectedOwner,
      },
    });
  } catch {
    return { ok: false, code: 'try-again' };
  }
  // Both epochs are checked after every await and immediately before
  // delivery (the mandatory durable reread inside bindingCurrent).
  if (!(await bindingCurrent(binding))) return { ok: false, code: 'account-changed' };
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    return { ok: false, code: mapServerCode(body?.error ?? 'try-again') };
  }
  const blob = await res.blob();
  if (!(await bindingCurrent(binding))) return { ok: false, code: 'account-changed' };
  return { ok: true, blob };
}
